import {
  SikAutoCompleteAPIResponseType,
  UniversalType,
} from "@coworker/apprestructured/src/scan-and-search/types/SikAutoCompleteAPIResponseType";
import { pickSIKlocale } from "@coworker/utils/sikLocales";
import {
  SIK_AUTOCOMPLETE,
  sikBaseUrlCoworker,
} from "@coworker/apprestructured/src/scan-and-search/constants/sikConstants";
import { SIK_API_VERSION, SIK_CLIENT_NAME } from "@coworker/consts/SIK";
import { useQuery } from "@tanstack/react-query";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useLanguage } from "@coworker/app/src/core/hooks/useLanguage";
import { useGetAPIKey } from "./fetch-coworker-search-api-key";

const transformAutoCompleteData = (
  data: SikAutoCompleteAPIResponseType
): string[] => {
  const completions: UniversalType[] = data?.searchBox?.universal;
  // new field returns completions and products and we are only interested in
  // completions for now (could also do map + filter but that does two round trips)
  return (completions || []).reduce((completions, item) => {
    if (item?.completion) {
      return completions.concat(item.completion?.text);
    }
    return completions;
  }, [] as string[]);
};

const fetchSikSearchBoxResults = async (
  primaryLocale: string,
  searchQuery: string,
  apiKey: string
) => {
  const sikLocale = pickSIKlocale(primaryLocale);
  const url = new URL(`${sikBaseUrlCoworker}/${sikLocale}/${SIK_AUTOCOMPLETE}`);

  url.searchParams.append("q", searchQuery);
  url.searchParams.append("v", SIK_API_VERSION);
  url.searchParams.append("c", SIK_CLIENT_NAME);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Access-Control-Allow-Origin": "*",
      "Access-Key": apiKey,
    },
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch SIK search box results: ${response.status} ${response.statusText}`
    );
  }
  let result;
  try {
    result = await response.json();
  } catch (err) {
    throw new Error("Failed to parse SIK search box response as JSON");
  }
  return result;
};

export const useCallSikSearchBox = (query: string) => {
  const store = useMyStore();
  const language = useLanguage();
  const { data: apiKey } = useGetAPIKey();
  const primaryLocale = store?.primaryLocale;

  return useQuery({
    queryKey: ["sikAutoComplete", primaryLocale, query],
    queryFn: () =>
      fetchSikSearchBoxResults(primaryLocale, query, apiKey as string),
    select: (data) => transformAutoCompleteData(data),
    staleTime: 0,
    enabled:
      query.length * (language === "zh_CN" ? 3 : 1) >= 2 && !!query && !!apiKey,
  });
};
